import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { ChevronDown, Facebook, Instagram, Youtube } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { heroSlides } from '../mock';

const SocialLinks = ({ links }) => {
  return (
    <div className="flex flex-col items-center gap-3 mt-4 animate-fade-in-up">
      <p className="text-white text-lg font-semibold">Follow us on:</p>
      <div className="flex flex-wrap gap-4 justify-center">
        {links.map((social) => {
          const IconComponent = social.icon;
          return (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`group flex items-center gap-3 bg-gradient-to-r ${social.color} text-white font-bold py-3 px-6 rounded-full text-base uppercase tracking-wider shadow-xl transition-all duration-300 hover:scale-110`}
            >
              <IconComponent className="w-6 h-6" />
              {social.name}
            </a>
          );
        })}
      </div>
    </div>
  );
};

export const HeroSection = () => {
  const [showSocialLinks, setShowSocialLinks] = useState(false);

  const scrollToNext = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleSocialLinks = () => {
    setShowSocialLinks(!showSocialLinks);
  };

  const socialLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://www.facebook.com/share/19wzZ7C95W/?mibextid=wwXIfr',
      color: 'from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://www.instagram.com/masterplanuae?igsh=MXF0OXp6bzk0cHhvYQ==',
      color: 'from-pink-600 to-purple-700 hover:from-pink-500 hover:to-purple-600'
    },
    {
      name: 'YouTube',
      icon: Youtube,
      url: 'https://youtube.com/@masterplanuae?si=oiI9Bqsc5jA4e5eP',
      color: 'from-red-600 to-red-700 hover:from-red-500 hover:to-red-600'
    }
  ];

  return (
    <section className="hero-section relative">
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.05); }
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translate(-50%, calc(-50% + 30px)); }
          100% { opacity: 0.95; transform: translate(-50%, -50%); }
        }
        .animate-fade-in-up { animation: fadeInUp 0.6s ease-out; }
        .hero-logo {
          position: absolute;
          top: 35%; 
          left: 50%;
          transform: translate(-50%, -50%);
          width: clamp(200px, 30vw, 450px);
          z-index: 10;
          opacity: 0.95;
          animation: fadeInUp 1.5s ease-out, pulse 2.2s ease-in-out 1.5s infinite;
          filter: brightness(0) invert(1);
          transition: filter 0.3s ease, transform 0.3s ease;
        }
        .hero-logo:hover {
          filter: brightness(0) invert(1) drop-shadow(0 0 20px rgba(255,255,255,0.5)) drop-shadow(0 0 40px rgba(255,255,255,0.25));
          transform: translate(-50%, -50%) scale(1.03);
        }
        .hero-fade {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 120px;
          background: linear-gradient(to bottom, transparent, #000);
          z-index: 5;
          pointer-events: none;
        }
      `}</style>

      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectFade]}
        slidesPerView={1}
        spaceBetween={0}
        loop={true}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={700}
        navigation={{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }}
        pagination={{ el: '.swiper-pagination', clickable: true, dynamicBullets: true }}
        className="h-screen"
        onSlideChange={() => setShowSocialLinks(false)}
      >
        {heroSlides.map((slide, index) => (
          <SwiperSlide key={slide.id}>
            <div className="relative h-screen w-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: slide.bgColor }}>
              {/* Background */}
              <div className="absolute inset-0 z-0">
                <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-purple-900/70 via-purple-900/50 to-black/80" />
              </div>

              {/* Slide Content */}
              {index === 0 ? (
                <>
                  <img src="/images/logo-bw.png" alt="MasterPlan Band" className="hero-logo" />
                  <div className="hero-fade"></div>

                  <div className="absolute z-10 left-1/2 -translate-x-1/2 flex flex-col items-center" style={{ top: '57%' }}>
                    <div className="flex flex-col items-center gap-4">
                      <button
                        onClick={toggleSocialLinks}
                        className="cta-button inline-flex items-center gap-2 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-black font-bold py-4 px-10 rounded-full text-lg uppercase tracking-wider shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105"
                      >
                        Connect with Us
                      </button>

                      {/* Smooth Expand/Collapse Social Links */}
                      <div className={`overflow-hidden transition-all duration-500 ${showSocialLinks ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <SocialLinks links={socialLinks} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="absolute z-10 w-full px-6 lg:px-16" style={{ top: '45%', transform: 'translateY(-50%)' }}>
                  <div className="flex flex-col items-center justify-center text-center max-w-4xl mx-auto gap-6">
                    <h1 className="hero-title text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-display uppercase font-extrabold tracking-tight" style={{ color: slide.textColor }}>
                      {slide.title}
                    </h1>
                    <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-wide max-w-3xl" style={{ color: '#FFFFFF', opacity: 0.9 }}>
                      {slide.description}
                    </p>

                    <div className="flex flex-col items-center gap-4 mt-4">
                      <button
                        onClick={toggleSocialLinks}
                        className="cta-button inline-flex items-center gap-2 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-black font-bold py-4 px-10 rounded-full text-lg uppercase tracking-wider shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-105"
                      >
                        Connect with Us
                      </button>

                      <div className={`overflow-hidden transition-all duration-500 ${showSocialLinks ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                        <SocialLinks links={socialLinks} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Scroll Indicator */}
              <button
                onClick={scrollToNext}
                className="absolute left-1/2 transform -translate-x-1/2 z-20 animate-bounce cursor-pointer bg-white/10 backdrop-blur-sm p-4 rounded-full hover:bg-white/20 transition-all duration-300"
                aria-label="Scroll to next section"
                style={{ bottom: '3rem' }}
              >
                <ChevronDown className="w-8 h-8 text-white" />
              </button>
            </div>
          </SwiperSlide>
        ))}

        {/* Navigation Buttons */}
        <div className="swiper-button-prev !text-green-400 !w-12 !h-12 after:!text-3xl" />
        <div className="swiper-button-next !text-green-400 !w-12 !h-12 after:!text-3xl" />

        {/* Pagination */}
        <div className="swiper-pagination" style={{ bottom: '7rem' }} />
      </Swiper>
    </section>
  );
};
